// The words on Home, as pure rules. Nothing here is AI: the loop line and the labels are the
// numbers said plainly, and "make another like this" is what the last post was, handed to Create.
import { CHANNEL_META, shapeLabel, type Channel, type PostFormat } from '@str/shared';
import type { HomeDto, HomeTile } from '@str/api/types';
import { saveNext, saveWizard } from './wizard';

const n = (v: number) => v.toLocaleString('en-US');

/** Channels report back within this many days of a post going out. */
export const REPORT_WINDOW_DAYS = 2;

/** Whether "channels report back within 2 days" is still true: the first post is that recent. */
function inReportWindow(firstPostAt: string | null | undefined, now: Date): boolean {
  if (!firstPostAt) return true;
  return now.getTime() - new Date(firstPostAt).getTime() < REPORT_WINDOW_DAYS * 86_400_000;
}

/** What a channel the platform has not opened to us is waiting on, in a few words. */
export function waitingOn(channel: Channel): string {
  if (channel === 'IG' || channel === 'FB') return 'Waiting on Meta’s insights approval';
  if (channel === 'TT') return 'Waiting on TikTok’s video access approval';
  return 'Not available yet';
}

/** Why a tile has no number: still counting inside the window, else the honest reason. */
export function notInYet(
  h: { firstPostAt: string | null; timezone: string | null; pending: HomeDto['pending'] },
  now = new Date(),
): { mark: string; detail: string | null } {
  if (inReportWindow(h.firstPostAt, now))
    return {
      mark: 'still counting',
      detail: h.firstPostAt ? firstPostLine(h.firstPostAt, h.timezone, now) : null,
    };
  return {
    mark: 'not available yet',
    detail: h.pending[0] ? waitingOn(h.pending[0].channel) : 'no channel has reported numbers',
  };
}

/** The sub-line: a post took a minute, it reached this many people, it brought these clicks. */
export function loopLine(
  h: {
    period: number;
    postsInPeriod: number;
    publishedTotal: number;
    tiles: Pick<HomeDto['tiles'], 'reached' | 'clicks'>;
    firstPostAt?: string | null;
    pending?: HomeDto['pending'];
  },
  now = new Date(),
): string {
  const posts = h.postsInPeriod;
  if (!posts)
    return h.publishedTotal
      ? `Nothing went out in the last ${h.period} days. Your best posts are still below; make another.`
      : '';
  const plural = posts === 1 ? 'post' : 'posts';
  if (h.tiles.reached.counting) {
    const lead = `${posts} ${plural} in ${h.period} days, about a minute${posts === 1 ? '' : ' each'}.`;
    if (inReportWindow(h.firstPostAt, now))
      return `${lead} Channels report back within ${REPORT_WINDOW_DAYS} days.`;
    const reasons = (h.pending ?? []).map((p) => p.reason);
    return reasons.length
      ? `${lead} Their numbers are not available yet: ${reasons.join('; ')}.`
      : `${lead} No channel has reported numbers for them yet.`;
  }
  return `${posts} ${plural} in ${h.period} days, about a minute of your time each. They reached ${n(h.tiles.reached.value)} people and brought ${n(h.tiles.clicks.value)} clicks.`;
}

/** "Guest quote · Carousel", "Reel", "Post": the shape when it has one, the format when it says something. */
export function kindLabel(p: { format: PostFormat; shapeId: string | null }): string {
  const shape = shapeLabel(p.shapeId);
  if (p.format === 'Reel') return shape ? `Reel · ${shape}` : 'Reel';
  const parts = [shape ?? 'Post'];
  if (p.format === 'Carousel' || p.format === 'Story') parts.push(p.format);
  return parts.join(' · ');
}

/** The host's day ("Thu Sep 3") and the slot they picked, or the clock in their zone. */
export function whenParts(
  publishedAt: string,
  scheduledTime: string | null,
  timezone: string | null,
): { day: string; time: string } {
  const d = new Date(publishedAt);
  const tz = timezone ?? 'UTC';
  const day = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
    .format(d)
    .replace(',', '');
  const time =
    scheduledTime ??
    new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(
      d,
    );
  return { day, time };
}

export function whenLabel(
  publishedAt: string,
  scheduledTime: string | null,
  timezone: string | null,
): string {
  const { day, time } = whenParts(publishedAt, scheduledTime, timezone);
  return `${day}, ${time}`;
}

export function channelsShort(channels: readonly Channel[]): string {
  return channels.join(', ');
}

/** Full names for a sentence; the sidebar's "Shorts" is YouTube when the channel is named. */
export const CHANNEL_NAMES: Record<Channel, string> = {
  IG: 'Instagram',
  FB: 'Facebook',
  TT: 'TikTok',
  YT: 'YouTube',
};

export function splitLabel(key: HomeTile['split'][number]['channel']): string {
  return key === 'other' ? 'Elsewhere' : CHANNEL_META[key].label;
}

export function deltaLabel(deltaPct: number | null): string | null {
  if (deltaPct === null) return null;
  return `${deltaPct > 0 ? '+' : ''}${deltaPct}%`;
}

/** "first post landed Sunday" within the week, else the date. */
export function firstPostLine(iso: string, timezone: string | null, now = new Date()): string {
  const d = new Date(iso);
  const tz = timezone ?? 'UTC';
  const days = (now.getTime() - d.getTime()) / 86_400_000;
  const when =
    days < 6.5
      ? new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long' }).format(d)
      : new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(
          d,
        );
  return `first post landed ${when}`;
}

export function usualLine(u: HomeDto['usual']): string | null {
  if (u.count < u.needed)
    return `After ${u.needed} posts this line says how it compares with your usual. ${u.count} so far.`;
  return u.sentence;
}

/** An SVG path for a small line of the period, scaled to its peak; flat along the bottom when empty. */
export function sparkPath(series: number[], w: number, h: number): string {
  if (!series.length) return '';
  const max = Math.max(...series) || 1;
  const step = series.length > 1 ? w / (series.length - 1) : 0;
  const pad = 2;
  const fmt = (v: number) => String(Math.round(v * 10) / 10);
  return series
    .map((v, i) => {
      const x = i * step;
      const y = h - pad - (v / max) * (h - pad * 2);
      return `${i ? 'L' : 'M'}${fmt(x)} ${fmt(y)}`;
    })
    .join(' ');
}

/**
 * Make another like this: the shape, the format, the slot and the channels of that post go to
 * Create; the photos are matched fresh and the moment is asked for. Nothing is copied.
 */
export function prefillFromPost(
  propertyId: string,
  post: {
    format: PostFormat;
    shapeId: string | null;
    scheduledTime: string | null;
    channels: readonly Channel[];
  },
): void {
  saveWizard(propertyId, {
    kind: post.format === 'Reel' ? 'Reel' : 'Post',
    path: 'ai',
    line: '',
    picks: [],
    shapeId: post.shapeId ?? 'caption',
    input: {},
  });
  saveNext(
    propertyId,
    post.scheduledTime && post.channels.length
      ? { time: post.scheduledTime, channels: [...post.channels] }
      : null,
  );
}
